import { useTranslation } from '@cobliteam/i18n';
import Typography from '@cobliteam/ui/components/Typography';
import RadioGroupItem, { RadioGroup } from '@cobliteam/ui/form/Radio';
import type { IconNames } from '@cobliteam/ui/icons';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';

import { CHECKLIST_TEMPLATE_ID, type ChecklistTemplateId, getChecklistTemplates } from '../checklistTemplates';

const PREVIEW_COUNT = 2;
const BLANK_VALUE = '__blank__';

const TEMPLATE_ICONS: Record<ChecklistTemplateId, IconNames> = {
  [CHECKLIST_TEMPLATE_ID.DAILY_EXIT]: 'clipboard-list',
  [CHECKLIST_TEMPLATE_ID.REFUELING]: 'fuel',
  [CHECKLIST_TEMPLATE_ID.RETURN]: 'rotate-ccw',
};

interface ChecklistTemplateStepProps {
  selectedId: string | null | undefined;
  onSelectedIdChange: (id: string | null) => void;
}

const ChecklistTemplateStep = ({ selectedId, onSelectedIdChange }: ChecklistTemplateStepProps) => {
  const { t } = useTranslation('checklists');
  const templates = getChecklistTemplates(t);

  return (
    <RadioGroup
      value={selectedId === null ? BLANK_VALUE : (selectedId ?? '')}
      onValueChange={(v) => onSelectedIdChange(v === BLANK_VALUE ? null : v)}
      className='grid grid-cols-2 gap-4 overflow-y-auto p-5'
    >
      {templates.map((tpl) => {
        const items = tpl.values.items;
        const preview = items.slice(0, PREVIEW_COUNT);
        const remaining = items.length - PREVIEW_COUNT;
        const isSelected = selectedId === tpl.id;

        return (
          <label
            key={tpl.id}
            htmlFor={`tpl-${tpl.id}`}
            className={cn(
              'flex cursor-pointer flex-col gap-3 rounded-xl border p-4 text-left transition-all',
              isSelected ? 'border-primary-7 bg-primary-2 ring-1 ring-primary-4' : 'border-gray-5 bg-white hover:border-primary-4 hover:bg-primary-2'
            )}
          >
            <div className='flex items-start justify-between'>
              <div className='flex size-10 items-center justify-center rounded-xl bg-primary-3'>
                <Icon name={TEMPLATE_ICONS[tpl.id]} className='text-primary-11' size='sm' />
              </div>
              <div className='flex items-center gap-2 self-center'>
                <Typography color='secondary' variant='text-sm'>
                  {t('templates.items_count', { count: items.length })}
                </Typography>
                <RadioGroupItem id={`tpl-${tpl.id}`} value={tpl.id} />
              </div>
            </div>
            <div>
              <Typography variant='heading-md'>{t(tpl.nameKey)}</Typography>
              <Typography variant='description-list-default' color='secondary'>
                {t(`templates.${tpl.id}_description`)}
              </Typography>
            </div>
            <div className='flex flex-col gap-0.5'>
              {preview.map((item) => (
                <Typography key={item.title} variant='description-list-default' color='secondary'>
                  {item.title}
                </Typography>
              ))}
              {remaining > 0 && (
                <Typography variant='description-list-default' color='secondary' className='text-gray-8'>
                  {t('templates.more_items', { count: remaining })}
                </Typography>
              )}
            </div>
          </label>
        );
      })}

      <label
        htmlFor='tpl-blank'
        className={cn(
          'flex cursor-pointer flex-col gap-3 rounded-xl border border-dashed p-4 text-left transition-all',
          selectedId === null ? 'border-primary-7 bg-primary-2 ring-1 ring-primary-4' : 'border-gray-5 bg-white hover:border-gray-7'
        )}
      >
        <div className='flex items-start justify-between'>
          <div className='flex size-10 items-center justify-center rounded-xl bg-gray-3'>
            <Icon name='plus' className='text-gray-9' size='sm' />
          </div>
          <RadioGroupItem id='tpl-blank' value={BLANK_VALUE} />
        </div>
        <div>
          <Typography variant='heading-md'>{t('templates.blank')}</Typography>
          <Typography variant='description-list-default' color='secondary'>
            {t('templates.blank_long_description')}
          </Typography>
        </div>
        <div className='mt-auto pt-4'>
          <Typography variant='description-list-default' color='secondary' className='text-gray-8'>
            {t('templates.blank_cta')}
          </Typography>
        </div>
      </label>
    </RadioGroup>
  );
};

export default ChecklistTemplateStep;
